/**
 * T1.5.B - DPO + Grievance Officer compliance posture (2026-05-24).
 *
 * Single IT-Admin governance dashboard:
 *   1. DPO designation status (DPDPA §8) - red banner when unassigned
 *   2. Organization context (residency, industry, cross-border)
 *   3. Grievance officer queue (DPDPA §13) - open count by status +
 *      recent grievances with SLA countdown
 *
 * Per dupe-recheck: new page (not extending IndiaMoats, which is for
 * tech moats like Aadhaar Vault). Pulls from Core via the new
 * /v1/foundational/compliance-posture endpoint that fans out to
 * sdk.settings.getOnboardingProfile() + sdk.grievances.list().
 *
 * Standalone mode (no Core integration): shows the empty-state with
 * a clear pointer to /admin/integrations/foctta-core.
 */
import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Inbox,
  Mail,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
  Users,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';
import { getCompliancePosture, type CompliancePostureResponse } from '@/lib/api';

const OPEN_STATUSES = new Set([
  'new',
  'acknowledged',
  'in_review',
  'identity_verified',
  'submitted_for_review',
  'escalated',
]);

function statusBadgeVariant(
  status: string,
): 'critical' | 'warning' | 'info' | 'success' | 'glass' {
  switch (status) {
    case 'escalated':
      return 'critical';
    case 'new':
    case 'submitted_for_review':
      return 'warning';
    case 'acknowledged':
    case 'in_review':
    case 'identity_verified':
      return 'info';
    case 'resolved':
    case 'closed':
      return 'success';
    default:
      return 'glass';
  }
}

function slaCountdown(deadline: string | null): { text: string; tone: 'safe' | 'soon' | 'breach' } {
  if (!deadline) return { text: '-', tone: 'safe' };
  const remaining = new Date(deadline).getTime() - Date.now();
  if (remaining < 0) {
    return {
      text: `breached ${Math.abs(Math.floor(remaining / (24 * 60 * 60 * 1000)))}d ago`,
      tone: 'breach',
    };
  }
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  if (days < 1) return { text: `${hours}h left`, tone: 'soon' };
  if (days < 3) return { text: `${days}d ${hours}h`, tone: 'soon' };
  return { text: `${days}d`, tone: 'safe' };
}

export function DpoAndGrievance() {
  const [data, setData] = useState<CompliancePostureResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const r = await getCompliancePosture();
      setData(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load compliance posture');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <PageContainer>
      <PageHeader
        title="Compliance Posture - DPO & Grievance Officer"
        subtitle="DPDPA §8 (DPO designation) + §13 (grievance officer) - surfaced from FOCTTA Core via the integration. Read-only mirror; updates flow through Core's onboarding + grievance workflows."
        badges={[
          { label: 'DPDPA §8 + §13', variant: 'info' },
        ]}
        actions={
          <>
            <FocttaCoreBadge />
            <Button variant="glass" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </>
        }
      />

      {error && (
        <div className="text-xs px-3 py-2 mb-4 rounded-md bg-red-500/10 border border-red-500/20 text-red-300 font-mono">
          {error}
        </div>
      )}

      {data && !data.coreIntegrated && (
        <GlassCard className="p-6 mb-4 border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium text-amber-200">
                FOCTTA Core not integrated
              </div>
              <p className="text-xs text-amber-200/70 mt-1">
                DPO designation + grievance officer queue live in FOCTTA Core's privacy/consent
                module. Connect Core at <code className="font-mono">/admin/integrations/foctta-core</code>{' '}
                to populate this dashboard.
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* DPO designation banner */}
      {data && data.coreIntegrated && data.dpo.designationStatus === 'unassigned' && (
        <GlassCard className="p-6 mb-4 border border-red-500/40 bg-red-500/5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-red-200">
                DPDPA §8 - Data Protection Officer NOT designated
              </div>
              <p className="text-xs text-red-200/70 mt-1">
                Significant Data Fiduciaries must designate a DPO. Complete the organization
                profile in FOCTTA Core under <code className="font-mono">/onboarding</code>.
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {data && data.coreIntegrated && data.dpo.designationStatus === 'partial' && (
        <GlassCard className="p-6 mb-4 border border-amber-500/40 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium text-amber-200">
                DPO marked appointed but name/email missing
              </div>
              <p className="text-xs text-amber-200/70 mt-1">
                Complete the DPO contact fields in FOCTTA Core's organization profile so the
                DPB filing pre-fill works.
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* DPO card */}
        <GlassCard className="p-6 lg:col-span-1">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Data Protection Officer
            </CardTitle>
            <CardDescription>DPDPA §8 statutory designation</CardDescription>
          </CardHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {data?.dpo.designationStatus === 'complete' ? (
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
              ) : data?.dpo.designationStatus === 'partial' ? (
                <UserCheck className="h-5 w-5 text-amber-400" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-red-400" />
              )}
              <Badge
                variant={
                  data?.dpo.designationStatus === 'complete'
                    ? 'success'
                    : data?.dpo.designationStatus === 'partial'
                      ? 'warning'
                      : 'critical'
                }
                className="text-[10px]"
              >
                {data?.dpo.designationStatus ?? '-'}
              </Badge>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Name
              </div>
              <div className="text-sm font-medium">{data?.dpo.name ?? '-'}</div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </div>
              <div className="text-sm font-mono">{data?.dpo.email ?? '-'}</div>
            </div>
          </div>
        </GlassCard>

        {/* Organization context */}
        <GlassCard className="p-6 lg:col-span-1">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Organization
            </CardTitle>
            <CardDescription>Sourced from Core onboarding</CardDescription>
          </CardHeader>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Legal name
              </div>
              <div className="font-medium">{data?.organization.legalName ?? '-'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Industry
              </div>
              <div className="font-medium">{data?.organization.industry ?? '-'}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Region
                </div>
                <div className="font-medium">{data?.organization.primaryRegion ?? '-'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Residency
                </div>
                <div className="font-medium">{data?.organization.dataResidency ?? '-'}</div>
              </div>
            </div>
            {data?.organization.crossBorderTransfer === true && (
              <Badge variant="warning" className="text-[10px]">
                Cross-border transfer enabled
              </Badge>
            )}
          </div>
        </GlassCard>

        {/* Grievance summary */}
        <GlassCard className="p-6 lg:col-span-1">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              Grievance Queue
            </CardTitle>
            <CardDescription>DPDPA §13 grievance officer</CardDescription>
          </CardHeader>
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-semibold tabular">{data?.grievances.open ?? 0}</div>
              <div className="text-xs text-muted-foreground">open</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data?.grievances.countsByStatus ?? {}).map(([status, count]) => (
                <Badge key={status} variant={statusBadgeVariant(status)} className="text-[10px]">
                  {status}: {count}
                </Badge>
              ))}
              {Object.keys(data?.grievances.countsByStatus ?? {}).length === 0 && (
                <span className="text-[10px] text-muted-foreground italic">no grievances</span>
              )}
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Recent grievances table */}
      <GlassCard className="p-0 mt-4 overflow-hidden">
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">Recent grievances</CardTitle>
          <CardDescription>
            Last {data?.grievances.recent.length ?? 0} grievances + SLA countdown
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-y border-border/40 font-medium">
          <div className="col-span-2">Reference</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Created</div>
          <div className="col-span-3 text-right">SLA</div>
        </div>
        {(data?.grievances.recent ?? []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 opacity-30 mb-3" />
            <p className="text-sm">
              {data?.coreIntegrated
                ? 'No grievances in the recent window.'
                : 'Connect FOCTTA Core to surface grievances.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {data?.grievances.recent.map((g) => {
              const sla = slaCountdown(g.slaDeadline);
              const isOpen = OPEN_STATUSES.has(g.status);
              return (
                <div
                  key={g.id}
                  className="grid grid-cols-12 px-4 py-3 text-xs items-center hover:bg-muted/30 transition-colors"
                >
                  <div className="col-span-2 font-mono text-[11px]">{g.referenceNumber}</div>
                  <div className="col-span-2 text-xs">{g.category ?? '-'}</div>
                  <div className="col-span-2">
                    <Badge variant={statusBadgeVariant(g.status)} className="text-[9px]">
                      {g.status}
                    </Badge>
                  </div>
                  <div className="col-span-3 text-[11px] text-muted-foreground tabular">
                    {new Date(g.createdAt).toLocaleString('en-IN', { hour12: false })}
                  </div>
                  <div className="col-span-3 text-right">
                    {isOpen ? (
                      <Badge
                        variant={
                          sla.tone === 'breach'
                            ? 'critical'
                            : sla.tone === 'soon'
                              ? 'warning'
                              : 'glass'
                        }
                        className="text-[10px] font-mono"
                      >
                        {sla.text}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>
    </PageContainer>
  );
}
